import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { Account } from '../../../accounting/model/account';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { CompanyBankAccountService } from '../../../invoice/service/data/company-bank-account.service';
import { CompanyBankAccount } from '../../model/company-bank-account';
import { Company } from '../../model/company';
import { CompanyService } from '../../service/data/company.service';

interface BankAccountMappingRow {
  bankAccount: CompanyBankAccount;
  form: FormGroup;
}

@Component({
  selector: 'app-company-account-plan-change',
  templateUrl: './company-account-plan-change.component.html',
  styleUrls: ['./company-account-plan-change.component.scss'],
  providers: [CompanyBankAccountService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyAccountPlanChangeComponent implements OnChanges, OnDestroy {
  @Input() company: Company;
  @Input() open = false;

  @Output() saved = new EventEmitter<AccountPlan>();
  @Output() cancelled = new EventEmitter<void>();

  form: FormGroup;
  accountPlans: AccountPlan[] = [];
  accountPlanSelection: SelectionItem[] = [];
  accountSelection: Account[] = [];
  mappingRows: BankAccountMappingRow[] = [];
  loading = false;
  saving = false;
  loadFailed = false;

  private readonly subscriptions = new Subscription();
  private accountPlanSubscription: Subscription;

  constructor(
    private readonly accountPlanService: AccountPlanService,
    private readonly bankAccountService: CompanyBankAccountService,
    private readonly companyService: CompanyService,
    private readonly formControlFactory: FormControlExFactory,
    private readonly messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue && this.company?.id) this.load();
  }

  ngOnDestroy(): void {
    this.accountPlanSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  get currentAccountPlanName(): string {
    return this.accountPlans.find(plan => plan.id === this.company?.accountPlanId)?.name ?? '';
  }

  get selectedAccountPlan(): AccountPlan | undefined {
    return this.accountPlans.find(plan => plan.id === this.form?.controls['accountPlanId']?.value);
  }

  cancel(): void {
    if (!this.saving) this.cancelled.emit();
  }

  save(): void {
    this.form?.markAllAsTouched();
    this.mappingRows.forEach(row => row.form.markAllAsTouched());
    if (!this.selectedAccountPlan || this.form.invalid || this.mappingRows.some(row => row.form.invalid) || this.saving) return;

    this.saving = true;
    this.subscriptions.add(
      this.companyService
        .changeAccountPlan(this.company.id, {
          accountPlanId: this.selectedAccountPlan.id,
          bankAccounts: this.mappingRows.map(row => ({
            companyBankAccountId: row.bankAccount.id,
            accountId: row.form.controls['account'].value.id,
          })),
        })
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({ next: () => this.saved.emit(this.selectedAccountPlan), error: () => undefined })
    );
  }

  private load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.form = null;
    this.mappingRows = [];
    this.accountSelection = [];

    this.subscriptions.add(
      forkJoin({
        accountPlans: this.accountPlanService.getExtensiveList(),
        bankAccounts: this.bankAccountService.getByCompany(this.company.id),
      })
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: ({ accountPlans, bankAccounts }) => this.initializeForm(accountPlans, bankAccounts),
          error: error => {
            this.loadFailed = true;
            this.messageService.showError(error);
          },
        })
    );
  }

  private initializeForm(accountPlans: AccountPlan[], bankAccounts: CompanyBankAccount[]): void {
    this.accountPlans = accountPlans;
    this.accountPlanSelection = accountPlans
      .filter(plan => plan.id !== this.company.accountPlanId)
      .map(plan => new SelectionItem(plan.name, plan.id));

    const accountPlanControl = this.formControlFactory.createMandatory('COMPANY.ACCOUNT_PLAN.NEW', null);
    this.form = new FormGroup({ accountPlanId: accountPlanControl });
    this.accountPlanSubscription?.unsubscribe();
    this.accountPlanSubscription = accountPlanControl.valueChanges.subscribe(() => this.buildMappings(bankAccounts));
  }

  private buildMappings(bankAccounts: CompanyBankAccount[]): void {
    const selectedPlan = this.selectedAccountPlan;
    this.accountSelection = selectedPlan?.accounts ?? [];
    this.mappingRows = selectedPlan
      ? bankAccounts.map(bankAccount => {
          const matchingAccount = selectedPlan.accounts.find(account => account.number === bankAccount.accountingAccountNumber);
          return {
            bankAccount,
            form: new FormGroup({
              account: this.formControlFactory.createMandatory('COMPANY.ACCOUNT_PLAN.NEW_ACCOUNT', matchingAccount ?? null),
            }),
          };
        })
      : [];
  }
}
